import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Icon from '../components/Icon'
import AdminUserRow from './AdminUserRow'
import InviteUserModal from '../components/InviteUserModal'
import ConfirmDialog from '../components/ConfirmDialog'
import {
  fetchUsers, getTokenPayload, inviteUser, removeUserAccount, setUserEnabled, updateUserRight,
  resendInvite, resetUserMfa, logout,
} from '../api/security'
import styles from './AdminUsersTab.module.css'

// Default sort groups Pending users first, then Active, then Disabled —
// alphabetical by name within each group. Toggling the Status header reverses
// the group order (Disabled, Active, Pending); toggling Name ignores status
// entirely and sorts flat alphabetically.
const STATUS_RANK = { pending: 0, active: 1, disabled: 2 }

/**
 * AdminUsersTab — User Management: workspace stat tiles, the user table, and
 * the invite flow. Backed by gaussiq-identity's /security/users routes (see
 * src/api/security.js) — that service already owns users/user_rights.
 */
export default function AdminUsersTab() {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [notice, setNotice] = useState(null)
  const [managingId, setManagingId] = useState(null)
  const [inviteOpen, setInviteOpen] = useState(false)
  const [inviting, setInviting] = useState(false)
  const [search, setSearch] = useState('')
  const [adminOnly, setAdminOnly] = useState(false)
  const [sortKey, setSortKey] = useState(/** @type {'status'|'name'} */ ('status'))
  const [sortDir, setSortDir] = useState(/** @type {'asc'|'desc'} */ ('asc'))
  const [confirmSelfAdminOff, setConfirmSelfAdminOff] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    let cancelled = false
    fetchUsers()
      .then(data => { if (!cancelled) setUsers(data) })
      .catch(err => { if (!cancelled) setError(err.message) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  const currentUserId = getTokenPayload()?.useruid
  // Reset 2FA is admin-gated — matches the backend's _is_admin gate on
  // POST .../mfa/reset (admin_right or superadmin_right).
  const rights = getTokenPayload()?.rights
  const isAdmin = Boolean(rights?.admin_right) || Boolean(rights?.superadmin_right)

  const activeCount = users.filter(u => u.status === 'active').length
  const adminCount = users.filter(u => u.rights.admin).length
  const pendingCount = users.filter(u => u.status === 'pending').length

  function toggleSort(key) {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  const { selfUser, otherUsers } = useMemo(() => {
    const q = search.trim().toLowerCase()
    const filtered = users.filter(u => {
      if (adminOnly && !u.rights.admin) return false
      if (q && !u.name.toLowerCase().includes(q) && !u.email.toLowerCase().includes(q)) return false
      return true
    })

    const self = filtered.find(u => Number(u.id) === Number(currentUserId)) ?? null
    const rest = filtered.filter(u => Number(u.id) !== Number(currentUserId))

    rest.sort((a, b) => {
      if (sortKey === 'name') {
        const cmp = a.name.localeCompare(b.name)
        return sortDir === 'asc' ? cmp : -cmp
      }
      // sortKey === 'status': asc = pending, active, disabled; desc reverses
      // the group order. Alpha-by-name tiebreak stays ascending either way —
      // reversing it too would make "reverse sort" harder to read, not easier.
      const rankA = sortDir === 'asc' ? STATUS_RANK[a.status] : 2 - STATUS_RANK[a.status]
      const rankB = sortDir === 'asc' ? STATUS_RANK[b.status] : 2 - STATUS_RANK[b.status]
      return rankA !== rankB ? rankA - rankB : a.name.localeCompare(b.name)
    })

    return { selfUser: self, otherUsers: rest }
  }, [users, search, adminOnly, sortKey, sortDir, currentUserId])

  async function updateRights(userId, key, value) {
    const prev = users
    setUsers(prev.map(u => u.id === userId ? { ...u, rights: { ...u.rights, [key]: value } } : u))
    try {
      await updateUserRight(userId, key, value)
    } catch (err) {
      setUsers(prev)
      setError(err.message)
    }
  }

  // Turning your OWN admin_right off logs you out immediately (the backend
  // allows the change — see routes/security.py — but this account loses
  // access to Administration the moment it takes effect, so there's nothing
  // useful left to show). Every other self-edit (other rights, or turning
  // admin_right ON) goes through the normal updateRights path.
  function handleSelfRightChange(key, value) {
    if (key === 'admin' && value === false) {
      setConfirmSelfAdminOff(true)
      return
    }
    updateRights(currentUserId, key, value)
  }

  async function confirmRemoveSelfAdmin() {
    setConfirmSelfAdminOff(false)
    try {
      await updateUserRight(currentUserId, 'admin', false)
    } catch (err) {
      setError(err.message)
      return
    }
    await logout()
    navigate('/login', { replace: true })
  }

  async function disableUser(userId) {
    const target = users.find(u => u.id === userId)
    if (!target) return
    const enabling = target.status === 'disabled'
    const prev = users
    setUsers(prev.map(u => u.id === userId ? { ...u, status: enabling ? 'active' : 'disabled' } : u))
    try {
      await setUserEnabled(userId, enabling)
    } catch (err) {
      setUsers(prev)
      setError(err.message)
    }
  }

  async function removeUser(userId) {
    const prev = users
    setManagingId(null)
    setUsers(prev.filter(u => u.id !== userId))
    try {
      // Soft removal: revokes login and marks removed_at, rather than deleting
      // the user row — other tables (e.g. skills.reviewed_by) may reference it.
      // Distinct from Disable: a removed user is filtered out of fetchUsers()
      // server-side, so it actually disappears on the next refresh instead of
      // just relabeling as Disabled.
      await removeUserAccount(userId)
    } catch (err) {
      setUsers(prev)
      setError(err.message)
    }
  }

  async function handleResendInvite(userId) {
    const target = users.find(u => u.id === userId)
    setError(null)
    setNotice(null)
    try {
      await resendInvite(userId)
      setNotice(`Invite resent${target ? ` to ${target.email}` : ''}.`)
    } catch (err) {
      setError(err.message)
    }
  }

  async function handleResetMfa(userId) {
    const target = users.find(u => u.id === userId)
    setError(null)
    setNotice(null)
    try {
      await resetUserMfa(userId)
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, mfaMethod: null, mfaEnrolled: false } : u))
      setNotice(`Two-factor authentication reset for ${target ? target.name : 'user'}. They've been notified by email.`)
    } catch (err) {
      setError(err.message)
    }
  }

  async function handleInvite({ fullName, email, rights }) {
    setInviting(true)
    setError(null)
    try {
      await inviteUser({ fullName, email, rights })
      const refreshed = await fetchUsers()
      setUsers(refreshed)
      setInviteOpen(false)
    } catch (err) {
      setError(err.message)
    } finally {
      setInviting(false)
    }
  }

  if (loading) {
    return <div className={styles.wrap}><div className={`${styles.panel} ${styles.loadingText}`}>Loading…</div></div>
  }

  return (
    <div className={styles.wrap}>
      {error && (
        <div className={`${styles.panel} ${styles.errorBanner}`} role="alert">
          <span>{error}</span>
          <button
            type="button"
            className={styles.errorDismiss}
            onClick={() => setError(null)}
            aria-label="Dismiss"
          >
            <Icon name="close" />
          </button>
        </div>
      )}

      {notice && (
        <div className={`${styles.panel} ${styles.noticeBanner}`} role="status">
          <span>{notice}</span>
          <button
            type="button"
            className={styles.errorDismiss}
            onClick={() => setNotice(null)}
            aria-label="Dismiss"
          >
            <Icon name="close" />
          </button>
        </div>
      )}

      {/* ── Stat tiles ─────────────────────────────── */}
      <div className={styles.statsRow}>
        <div className={styles.statCard}>
          <span className={`${styles.statIcon} ${styles.statIconBlue}`}><Icon name="people" /></span>
          <div className={styles.statBody}>
            <span className={styles.statLabel}>Active Users</span>
            <span className={styles.statValue}>{activeCount}</span>
          </div>
        </div>
        <div className={styles.statCard}>
          <span className={`${styles.statIcon} ${styles.statIconBlue}`}><Icon name="lock" /></span>
          <div className={styles.statBody}>
            <span className={styles.statLabel}>Administrators</span>
            <span className={styles.statValue}>{adminCount}</span>
          </div>
        </div>
        <div className={styles.statCard}>
          <span className={`${styles.statIcon} ${styles.statIconGreen}`}><Icon name="mail" /></span>
          <div className={styles.statBody}>
            <span className={styles.statLabel}>Pending Invites</span>
            <span className={`${styles.statValue} ${styles.statValueGreen}`}>{pendingCount}</span>
          </div>
        </div>
      </div>

      {/* ── Users panel ────────────────────────────── */}
      <div className={styles.panel}>
        <div className={styles.panelHeader}>
          <div>
            <h2 className={styles.panelTitle}>Users</h2>
          </div>
          <button type="button" className={styles.inviteBtn} onClick={() => setInviteOpen(true)}>
            Invite user
          </button>
        </div>

        {/* ── Search + admin-only filter ─────────────── */}
        <div className={styles.toolbar}>
          <div className={styles.searchWrap}>
            <Icon name="search" className={styles.searchIcon} />
            <input
              className={styles.search}
              type="text"
              placeholder="Search by name or email"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Escape' && setSearch('')}
            />
          </div>
          <label className={styles.adminOnlyLabel}>
            <input
              type="checkbox"
              checked={adminOnly}
              onChange={e => setAdminOnly(e.target.checked)}
            />
            Admins only
          </label>
        </div>

        <div className={styles.tableHead}>
          <button type="button" className={`${styles.colName} ${styles.sortableHead}`} onClick={() => toggleSort('name')}>
            Name
            {sortKey === 'name' && <Icon name={sortDir === 'asc' ? 'chevron-up' : 'chevron-down'} className={styles.sortIcon} />}
          </button>
          <span className={styles.colEmail}>Email</span>
          <span className={styles.colMfa} title="Two-factor authentication">2FA</span>
          <span className={styles.colLogin}>Last Login</span>
          <button type="button" className={`${styles.colStatus} ${styles.sortableHead}`} onClick={() => toggleSort('status')}>
            Status
            {sortKey === 'status' && <Icon name={sortDir === 'asc' ? 'chevron-up' : 'chevron-down'} className={styles.sortIcon} />}
          </button>
          <span className={styles.colActions} />
        </div>

        <div className={styles.rows}>
          {/* Your own row is pinned first (still subject to the search/admin-
              only filters above it, just not to the Name/Status sort) — you
              can view and edit your own permissions here now, instead of only
              via the navbar's account menu. */}
          {selfUser && (
            <AdminUserRow
              key={selfUser.id}
              user={selfUser}
              isSelf
              expanded={managingId === selfUser.id}
              canResetMfa={isAdmin}
              onToggleManage={() => setManagingId(prev => prev === selfUser.id ? null : selfUser.id)}
              onRightChange={handleSelfRightChange}
            />
          )}
          {otherUsers.map(u => (
            <AdminUserRow
              key={u.id}
              user={u}
              expanded={managingId === u.id}
              canResetMfa={isAdmin}
              onToggleManage={() => setManagingId(prev => prev === u.id ? null : u.id)}
              onRightChange={(key, value) => updateRights(u.id, key, value)}
              onDisable={() => disableUser(u.id)}
              onRemove={() => removeUser(u.id)}
              onResetMfa={() => handleResetMfa(u.id)}
              onResendInvite={() => handleResendInvite(u.id)}
            />
          ))}
        </div>
      </div>

      {inviteOpen && (
        <InviteUserModal
          existingEmails={users.map(u => u.email)}
          onCancel={() => !inviting && setInviteOpen(false)}
          onInvite={handleInvite}
        />
      )}

      {confirmSelfAdminOff && (
        <ConfirmDialog
          title="Remove your own admin access"
          message="You're about to remove your own admin rights. You'll be logged out immediately and will need another admin to restore permissions if you change your mind."
          confirmLabel="Remove admin access"
          variant="danger"
          icon="warning"
          onConfirm={confirmRemoveSelfAdmin}
          onCancel={() => setConfirmSelfAdminOff(false)}
        />
      )}
    </div>
  )
}
